import React from 'react';
import { useParams, Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import axios from 'axios';
import PublicPageLayout from '../components/PublicPageLayout';
import { ChevronRight, Calendar, User, ArrowLeft, BookOpen } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL || '';

function renderBlogContent(raw) {
  if (!raw) return null;
  const sections = raw.split('\n\n');
  return sections.map((section, i) => {
    const trimmed = section.trim();
    if (!trimmed) return null;
    // All-caps heading
    if (/^[A-Z][A-Z\s&':-]{4,}$/.test(trimmed)) {
      return <h2 key={i} className="text-lg font-bold text-slate-900 mt-8 mb-3">{trimmed}</h2>;
    }
    // Numbered item: "1. TITLE - detail"
    if (/^\d+\.\s+[A-Z]/.test(trimmed)) {
      return <h3 key={i} className="text-base font-semibold text-slate-800 mt-6 mb-2">{trimmed}</h3>;
    }
    // Bullet list
    if (trimmed.startsWith('- ')) {
      const items = trimmed.split('\n').filter(l => l.startsWith('- '));
      return (
        <ul key={i} className="space-y-2 my-4">
          {items.map((item, j) => (
            <li key={j} className="flex items-start gap-2.5 text-sm text-slate-600 leading-relaxed">
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 mt-2 flex-shrink-0" />
              {item.slice(2)}
            </li>
          ))}
        </ul>
      );
    }
    return <p key={i} className="text-sm text-slate-600 leading-relaxed mb-4">{trimmed}</p>;
  });
}

export default function BlogPostPage() {
  const { slug } = useParams();

  const { data: blog, isLoading, error } = useQuery({
    queryKey: ['blog', slug],
    queryFn: async () => {
      const { data } = await axios.get(`${API}/api/blogs/${slug}`);
      return data;
    },
  });

  return (
    <PublicPageLayout>
      <div className="max-w-3xl mx-auto px-6 py-12" data-testid="blog-post-page">
        {/* Breadcrumb */}
        <div className="flex items-center gap-2 text-sm text-slate-400 mb-8">
          <Link to="/" className="hover:text-slate-600 transition-colors">Home</Link>
          <ChevronRight className="w-3.5 h-3.5" />
          <Link to="/blog" className="hover:text-slate-600 transition-colors">Blog</Link>
          <ChevronRight className="w-3.5 h-3.5" />
          <span className="text-slate-600 font-medium truncate max-w-[200px]">{blog?.title || 'Loading...'}</span>
        </div>

        {isLoading ? (
          <div className="flex items-center justify-center py-20">
            <div className="w-8 h-8 border-2 border-emerald-500 border-t-transparent rounded-full animate-spin" />
          </div>
        ) : error ? (
          <div className="text-center py-20">
            <BookOpen className="w-12 h-12 text-slate-300 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-slate-700 mb-2">Post not found</h3>
            <Link to="/blog" className="text-sm text-emerald-600 hover:text-emerald-700 font-medium">Back to Blog</Link>
          </div>
        ) : (
          <article>
            {/* Header */}
            <div className="mb-10">
              {blog.category && (
                <span className="inline-block px-3 py-1 bg-emerald-50 text-emerald-700 rounded-md text-xs font-medium mb-4">
                  {blog.category}
                </span>
              )}
              <h1 className="text-2xl sm:text-3xl font-bold text-slate-900 mb-4 leading-tight">{blog.title}</h1>
              <div className="flex items-center gap-4 text-sm text-slate-400">
                {blog.author && (
                  <span className="flex items-center gap-1.5"><User className="w-4 h-4" />{blog.author}</span>
                )}
                {blog.created_at && (
                  <span className="flex items-center gap-1.5">
                    <Calendar className="w-4 h-4" />
                    {new Date(blog.created_at).toLocaleDateString('en-AU', { year: 'numeric', month: 'long', day: 'numeric' })}
                  </span>
                )}
              </div>
            </div>

            {/* Divider */}
            <div className="h-px bg-slate-200 mb-10" />

            {/* Content */}
            <div className="blog-content">{renderBlogContent(blog.content)}</div>

            {/* Back Link */}
            <div className="mt-12 pt-8 border-t border-slate-200">
              <Link to="/blog" className="inline-flex items-center gap-2 text-sm font-medium text-emerald-600 hover:text-emerald-700 transition-colors" data-testid="blog-post-back">
                <ArrowLeft className="w-4 h-4" /> Back to all posts
              </Link>
            </div>
          </article>
        )}
      </div>
    </PublicPageLayout>
  );
}
